/**
 * The live preview of a business's public page — Site & widget and the setup
 * wizard draw it — and the contract between the two sides of it.
 *
 * The preview is the real Site1 template in a same-origin iframe, so that a
 * phone preview is laid out by a phone-sized viewport: the template's
 * breakpoints are media queries, and they answer to the window, not to the box
 * the page happens to be drawn in. The frame (`/preview/site`) is mounted by
 * `index.jsx` instead of `<App>`, like the booking widget: it needs no session,
 * no routes and no chrome, only the settings being edited.
 *
 * Those settings — unsaved, with unsaved images as blob: URLs — arrive by
 * postMessage, both ways restricted to our own origin: the dashboard accepts
 * messages only from the frame it created, and the frame only from the window
 * that framed it. nginx already refuses to let any other site frame a path
 * outside `/embed/`.
 */

export const SITE_PREVIEW_PATH = "/preview/site";

/** Marks our messages, so neither side mistakes another script's for ours. */
export const SITE_PREVIEW_SOURCE = "easyplanner-site-preview";

/** The sections the preview can point at; anything else in a message is ignored. */
export const SITE_SECTIONS = ["header", "about", "contact", "social", "brand"];

/** Two segments, so no business slug (`/:organizationSlug`) can ever shadow it. */
export const isSitePreviewPath = (pathname) =>
  typeof pathname === "string" && pathname.replace(/\/+$/, "") === SITE_PREVIEW_PATH;

const sameOrigin = (event) =>
  event.origin === window.location.origin && event.data?.source === SITE_PREVIEW_SOURCE;

/** In the frame: was this sent by the dashboard page that framed us? */
export const isFromHost = (event) =>
  window.parent !== window && event.source === window.parent && sameOrigin(event);

/** In the dashboard: was this sent by our own preview frame? */
export const isFromFrame = (event, frameWindow) =>
  Boolean(frameWindow) && event.source === frameWindow && sameOrigin(event);

/** Frame → dashboard. Same origin only: the payload is the business's draft. */
export const postToHost = (type, payload = {}) => {
  if (window.parent === window) return;
  window.parent.postMessage(
    { source: SITE_PREVIEW_SOURCE, type, ...payload },
    window.location.origin,
  );
};

/** Dashboard → frame. */
export const postToFrame = (frameWindow, type, payload = {}) => {
  frameWindow?.postMessage(
    { source: SITE_PREVIEW_SOURCE, type, ...payload },
    window.location.origin,
  );
};
